import { useCallback, useEffect, useMemo, useState } from 'react'

// TODO: Admin view for team instances - to be implemented later
// This component will allow team owners/admins to view and manage all team member instances
// Features planned: bulk operations, cross-member filtering, team-wide statistics
import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server'
import { Divider, EmptyView, Loading, Pagination, Trans } from '@/components'
import { FilterItem } from '@/hooks/useInstanceList'
import {
  Instance,
  ReleasedStatuses,
  RunningStatuses,
  SnapshottingStatuses,
} from '@/models/instance'
import { useTeamStore } from '@/stores/team'

import { AdminFilters } from './AdminFilters'
import { AdminInstance, AdminInstanceTable } from './AdminInstanceTable'
import { AdminStatsCards } from './AdminStatsCards'
import { BulkAction, BulkEditModal } from './BulkEditModal'

const REGION_GLOBAL = -1
const REGION_DATA_DRIVE = -2
const REGION_MESH_CLUSTERING = -3

const statusFilters: FilterItem[] = [
  {
    label: <Trans i18nKey="instance.list.statusLabelRunning">Running</Trans>,
    value: 'Running',
    codes: RunningStatuses,
  },
  {
    label: <Trans i18nKey="instance.list.statusLabelSnapshotting">Snapshotting</Trans>,
    value: 'Snapshotting',
    codes: SnapshottingStatuses,
  },
  {
    label: <Trans i18nKey="instance.list.statusLabelReleased">Released</Trans>,
    value: 'Released',
    codes: ReleasedStatuses,
  },
]

interface AdminInstanceListViewProps {
  hideHeader?: boolean
}

export function AdminInstanceListView({ hideHeader = false }: AdminInstanceListViewProps) {
  const teamStore = useTeamStore()
  const teamTokenData = teamStore.teamTokenData

  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<(string | number)[]>(['all' as const])
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [regions, setRegions] = useState<number[]>([])
  const [regionFilterCriteria, setRegionFilterCriteria] = useState<number>(REGION_GLOBAL)
  const [instances, setInstances] = useState<AdminInstance[]>([])
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null)

  const pageSize = 5

  // Fetch domains
  useEffect(() => {
    API.getDomainList().then((res) => {
      if (res.code === 0) {
        setDomainInfos(res.domainInfos || [])
        setRegions((res.domainInfos || []).map((info) => info.domain))
      }
    })
  }, [])

  // Fetch team member instances
  const fetchInstances = useCallback(
    async (p: number = page) => {
      const teamResID = teamTokenData?.teamResID
      if (!teamResID) return

      setLoading(true)
      try {
        let apiRegionFilterCriteria = 0
        if (regionFilterCriteria === REGION_GLOBAL) {
          apiRegionFilterCriteria = 0
        } else if (regionFilterCriteria === REGION_DATA_DRIVE) {
          apiRegionFilterCriteria = 1
        } else if (regionFilterCriteria === REGION_MESH_CLUSTERING) {
          apiRegionFilterCriteria = 2
        }

        // Calculate statuses array from selected status filters
        let apiStatuses: number[] | undefined = undefined
        if (!selectedStatuses.includes('all')) {
          apiStatuses = selectedStatuses.flatMap(
            (value) => statusFilters.find((f) => f.value === value)?.codes || [],
          )
        }

        const res = await API.getTeamMemberInstances({
          res_id: teamResID,
          page: p,
          per_page: pageSize,
          statuses: apiStatuses,
          regions: regions,
          region_filter_criteria: apiRegionFilterCriteria,
          member_res_ids: selectedMembers.length > 0 ? selectedMembers : undefined,
        })

        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)

          const adminInstances: AdminInstance[] = res.instances.map((item) => {
            const instance = new Instance(item.instance)
            return {
              id: instance.id,
              resID: instance.resID,
              displayTitle: instance.displayTitle,
              domain: instance.domain,
              owner: {
                resID: item.user.resID,
                name: item.user.user.name,
                nickname: item.user.user.nickname,
              },
              statusKey: instance.statusKey,
              statusText: instance.statusText,
              statusTextStyle: instance.statusTextStyle,
              cost: instance.cost,
              canRelease: instance.canRelease,
              _fullData: item, // Preserve full data reference
            }
          })

          setInstances(adminInstances)
        }
      } finally {
        setLoading(false)
      }
    },
    [teamTokenData, page, selectedStatuses, regions, regionFilterCriteria, selectedMembers],
  )

  useEffect(() => {
    fetchInstances(1)
  }, [selectedStatuses, regions, regionFilterCriteria, selectedMembers])

  const selectedInstances = useMemo(
    () => instances.filter((ins) => selectedInstanceIds.includes(ins.resID)),
    [instances, selectedInstanceIds],
  )

  const allCanRelease = useMemo(() => {
    if (selectedInstanceIds.length === 0) return false
    return selectedInstances.every((ins) => ins.canRelease)
  }, [selectedInstanceIds, selectedInstances])

  const handleBulkEdit = (action: BulkAction) => {
    if (selectedInstanceIds.length === 0) return
    setBulkAction(action)
  }

  const handleBulkConfirm = () => {
    setBulkAction(null)
    setSelectedInstanceIds([])
    fetchInstances()
  }

  return (
    <div className="h-full overflow-y-auto">
      {!hideHeader && (
        <>
          <div className="flex h-[60px] items-center justify-start gap-2">
            <div className="h6">
              <Trans i18nKey="instance.title">My Instances</Trans>
            </div>
          </div>

          <Divider />
        </>
      )}

      <AdminStatsCards />

      <AdminFilters
        selectedMembers={selectedMembers}
        onMembersChange={setSelectedMembers}
        statusFilters={statusFilters}
        selectedStatuses={selectedStatuses}
        onStatusChange={setSelectedStatuses}
        domainInfos={domainInfos}
        region={regionFilterCriteria}
        onRegionChange={(region, newRegions) => {
          setRegionFilterCriteria(region)
          setRegions(newRegions)
        }}
        REGION_GLOBAL={REGION_GLOBAL}
        REGION_DATA_DRIVE={REGION_DATA_DRIVE}
        REGION_MESH_CLUSTERING={REGION_MESH_CLUSTERING}
        selectedCount={selectedInstanceIds.length}
        allCanRelease={allCanRelease}
        onBulkEdit={handleBulkEdit}
      />

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!loading && instances.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}

      {!loading && instances.length > 0 && (
        <>
          <AdminInstanceTable
            instances={instances}
            domainInfos={domainInfos}
            selectedIds={selectedInstanceIds}
            onSelectionChange={setSelectedInstanceIds}
            onUpdate={() => fetchInstances()}
          />

          <div className="flex justify-between items-center mt-4">
            <div className="t-sm text-secondary">
              {selectedInstanceIds.length > 0 ? (
                <Trans
                  i18nKey="team.admin.selected"
                  values={{ count: selectedInstanceIds.length, total }}
                >
                  Selected {selectedInstanceIds.length} / {total}
                </Trans>
              ) : (
                <span>&nbsp;</span>
              )}
            </div>

            <Pagination
              size={pageSize}
              total={total}
              current={page}
              onChange={(newPage) => fetchInstances(newPage)}
            />
          </div>
        </>
      )}

      {bulkAction && (
        <BulkEditModal
          open={true}
          action={bulkAction}
          instances={selectedInstances}
          onClose={() => setBulkAction(null)}
          onConfirm={handleBulkConfirm}
        />
      )}
    </div>
  )
}
